import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  TextInput,
  Alert,
  Platform,
  Modal,
  ActivityIndicator,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { useToast } from '../contexts/ToastContext';
import {
  getClubLocations,
  createClubLocation,
  updateClubLocation,
  setClubLocationActive,
  deleteClubLocation,
  updateClubMainLocation,
} from '../services/database';
import { useTheme } from '../contexts/ThemeContext';
import { getColors } from '../styles/theme';

const ClubLocationManager = ({ clubId, currentLocation, onLocationUpdated }) => {
  const { showToast } = useToast();
  const { isDarkMode } = useTheme();
  const colors = getColors(isDarkMode);
  
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    fetchLocations();
  }, [clubId]);

  useEffect(() => {
    if (currentLocation) {
      setLatitude(currentLocation.latitude?.toString() || '');
      setLongitude(currentLocation.longitude?.toString() || '');
      setAddress(currentLocation.address || '');
    }
  }, [currentLocation]);

  const fetchLocations = async () => {
    try {
      setLoading(true);
      const data = await getClubLocations(clubId);
      setLocations(data || []);
    } catch (error) {
      console.error('Error fetching locations:', error);
      showToast('Failed to load locations', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveLocation = async () => {
    if (!name.trim()) {
      showToast('Please enter a name for this location', 'error');
      return;
    }

    const lat = parseFloat(latitude);
    const lng = parseFloat(longitude);

    if (isNaN(lat) || isNaN(lng)) {
      showToast('Please enter valid coordinates', 'error');
      return;
    }

    try {
      setSaving(true);

      if (editingLocation) {
        // Update existing location
        await updateClubLocation(editingLocation.id, {
          name: name.trim(),
          address: address.trim() || null,
          latitude: lat,
          longitude: lng,
          notes: notes.trim() || null,
        });
        showToast('Location updated', 'success');
      } else {
        // Create new location
        await createClubLocation(clubId, {
          name: name.trim(),
          address: address.trim() || null,
          latitude: lat,
          longitude: lng,
          notes: notes.trim() || null,
          setAsActive: false,
        });
        showToast('Location saved', 'success');
      }

      // Reset form
      setName('');
      setAddress('');
      setLatitude(currentLocation?.latitude?.toString() || '');
      setLongitude(currentLocation?.longitude?.toString() || '');
      setNotes('');
      setEditingLocation(null);
      setShowAddModal(false);
      
      // Refresh locations
      await fetchLocations();
      if (onLocationUpdated) {
        onLocationUpdated();
      }
    } catch (error) {
      console.error('Error saving location:', error);
      showToast('Failed to save location', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSetActive = async (locationId) => {
    try {
      await setClubLocationActive(clubId, locationId);
      showToast('Location activated', 'success');
      await fetchLocations();
      if (onLocationUpdated) {
        onLocationUpdated();
      }
    } catch (error) {
      console.error('Error activating location:', error);
      showToast('Failed to activate location', 'error');
    }
  };

  const handleDelete = async (locationId) => {
    const confirmDelete = Platform.OS === 'web'
      ? window.confirm('Are you sure you want to delete this location?')
      : await new Promise((resolve) => {
          Alert.alert(
            'Delete Location',
            'Are you sure you want to delete this location?',
            [
              { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
              { text: 'Delete', style: 'destructive', onPress: () => resolve(true) },
            ]
          );
        });

    if (!confirmDelete) return;

    try {
      await deleteClubLocation(locationId);
      showToast('Location deleted', 'success');
      await fetchLocations();
    } catch (error) {
      console.error('Error deleting location:', error);
      showToast('Failed to delete location', 'error');
    }
  };

  const handleEdit = (location) => {
    setEditingLocation(location);
    setName(location.name);
    setAddress(location.address || '');
    setLatitude(location.latitude.toString());
    setLongitude(location.longitude.toString());
    setNotes(location.notes || '');
    setShowAddModal(true);
  };

  const handleUseCurrentLocation = () => {
    if (currentLocation?.latitude && currentLocation?.longitude) {
      setLatitude(currentLocation.latitude.toString());
      setLongitude(currentLocation.longitude.toString());
      setAddress(currentLocation.address || '');
    }
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={[styles.title, { color: colors.text }]}>Saved Locations</Text>
        <TouchableOpacity
          style={[styles.addButton, { backgroundColor: colors.primary }]}
          onPress={() => {
            setEditingLocation(null);
            setName('');
            setAddress('');
            setLatitude(currentLocation?.latitude?.toString() || '');
            setLongitude(currentLocation?.longitude?.toString() || '');
            setNotes('');
            setShowAddModal(true);
          }}
        >
          <Icon name="add" size={20} color="#fff" />
          <Text style={styles.addButtonText}>Add Location</Text>
        </TouchableOpacity>
      </View>

      {locations.length === 0 ? (
        <View style={styles.emptyState}>
          <Icon name="location-on" size={48} color={colors.textSecondary} />
          <Text style={[styles.emptyStateText, { color: colors.textSecondary }]}>
            No saved locations yet
          </Text>
          <Text style={[styles.emptyStateSubtext, { color: colors.textSecondary }]}>
            Save locations to quickly switch between venues
          </Text>
        </View>
      ) : (
        <ScrollView style={styles.locationsList}>
          {locations.map((location) => (
            <View
              key={location.id}
              style={[styles.locationCard, { backgroundColor: colors.surface, borderColor: colors.border }]}
            >
              <View style={styles.locationHeader}>
                <View style={styles.locationInfo}>
                  <Text style={[styles.locationName, { color: colors.text }]}>
                    {location.name}
                  </Text>
                  {location.is_active && (
                    <View style={[styles.activeBadge, { backgroundColor: colors.success }]}>
                      <Text style={styles.activeBadgeText}>Active</Text>
                    </View>
                  )}
                </View>
                <View style={styles.locationActions}>
                  {!location.is_active && (
                    <TouchableOpacity
                      style={[styles.actionButton, { backgroundColor: colors.primary }]}
                      onPress={() => handleSetActive(location.id)}
                    >
                      <Icon name="check-circle" size={20} color="#fff" />
                    </TouchableOpacity>
                  )}
                  <TouchableOpacity
                    style={[styles.actionButton, { backgroundColor: colors.secondary }]}
                    onPress={() => handleEdit(location)}
                  >
                    <Icon name="edit" size={18} color="#fff" />
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.actionButton, { backgroundColor: colors.error }]}
                    onPress={() => handleDelete(location.id)}
                  >
                    <Icon name="delete" size={18} color="#fff" />
                  </TouchableOpacity>
                </View>
              </View>
              
              {location.address && (
                <Text style={[styles.locationAddress, { color: colors.textSecondary }]}>
                  {location.address}
                </Text>
              )}
              
              <Text style={[styles.locationCoords, { color: colors.textSecondary }]}>
                {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
              </Text>
              
              {location.notes && (
                <Text style={[styles.locationNotes, { color: colors.textSecondary }]}>
                  {location.notes}
                </Text>
              )}
            </View>
          ))}
        </ScrollView>
      )}

      {/* Add/Edit Modal */}
      <Modal
        visible={showAddModal}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setShowAddModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { backgroundColor: colors.surface }]}>
            <View style={styles.modalHeader}>
              <Text style={[styles.modalTitle, { color: colors.text }]}>
                {editingLocation ? 'Edit Location' : 'Add Location'}
              </Text>
              <TouchableOpacity
                onPress={() => setShowAddModal(false)}
                style={styles.closeButton}
              >
                <Icon name="close" size={24} color={colors.text} />
              </TouchableOpacity>
            </View>

            <ScrollView style={styles.modalBody}>
              <View style={styles.inputGroup}>
                <Text style={[styles.label, { color: colors.text }]}>Name *</Text>
                <TextInput
                  style={[styles.input, { backgroundColor: colors.background, color: colors.text, borderColor: colors.border }]}
                  value={name}
                  onChangeText={setName}
                  placeholder="e.g., Main Cafe, Weekly Bar Night"
                  placeholderTextColor={colors.textSecondary}
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={[styles.label, { color: colors.text }]}>Address</Text>
                <TextInput
                  style={[styles.input, { backgroundColor: colors.background, color: colors.text, borderColor: colors.border }]}
                  value={address}
                  onChangeText={setAddress}
                  placeholder="Optional address"
                  placeholderTextColor={colors.textSecondary}
                />
              </View>

              <View style={styles.coordRow}>
                <View style={[styles.inputGroup, styles.coordInput]}>
                  <Text style={[styles.label, { color: colors.text }]}>Latitude *</Text>
                  <TextInput
                    style={[styles.input, { backgroundColor: colors.background, color: colors.text, borderColor: colors.border }]}
                    value={latitude}
                    onChangeText={setLatitude}
                    keyboardType="numeric"
                    placeholder="49.2827"
                    placeholderTextColor={colors.textSecondary}
                  />
                </View>
                <View style={[styles.inputGroup, styles.coordInput]}>
                  <Text style={[styles.label, { color: colors.text }]}>Longitude *</Text>
                  <TextInput
                    style={[styles.input, { backgroundColor: colors.background, color: colors.text, borderColor: colors.border }]}
                    value={longitude}
                    onChangeText={setLongitude}
                    keyboardType="numeric"
                    placeholder="-123.1207"
                    placeholderTextColor={colors.textSecondary}
                  />
                </View>
              </View>

              {currentLocation?.latitude && (
                <TouchableOpacity
                  style={[styles.useCurrentButton, { backgroundColor: colors.secondary }]}
                  onPress={handleUseCurrentLocation}
                >
                  <Icon name="my-location" size={18} color="#fff" />
                  <Text style={styles.useCurrentButtonText}>Use Current Location</Text>
                </TouchableOpacity>
              )}

              <View style={styles.inputGroup}>
                <Text style={[styles.label, { color: colors.text }]}>Notes</Text>
                <TextInput
                  style={[styles.input, styles.textArea, { backgroundColor: colors.background, color: colors.text, borderColor: colors.border }]}
                  value={notes}
                  onChangeText={setNotes}
                  placeholder="Optional notes about this location"
                  placeholderTextColor={colors.textSecondary}
                  multiline
                  numberOfLines={3}
                />
              </View>
            </ScrollView>

            <View style={styles.modalFooter}>
              <TouchableOpacity
                style={[styles.cancelButton, { borderColor: colors.border }]}
                onPress={() => setShowAddModal(false)}
              >
                <Text style={[styles.cancelButtonText, { color: colors.text }]}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.saveButton, { backgroundColor: colors.primary }]}
                onPress={handleSaveLocation}
                disabled={saving}
              >
                {saving ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <Text style={styles.saveButtonText}>Save</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    gap: 6,
  },
  addButtonText: {
    color: '#fff',
    fontWeight: '600',
    fontSize: 14,
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  emptyStateText: {
    fontSize: 18,
    fontWeight: '600',
    marginTop: 16,
    marginBottom: 8,
  },
  emptyStateSubtext: {
    fontSize: 14,
    textAlign: 'center',
  },
  locationsList: {
    flex: 1,
  },
  locationCard: {
    padding: 16,
    margin: 16,
    marginBottom: 8,
    borderRadius: 12,
    borderWidth: 1,
  },
  locationHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  locationInfo: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  locationName: {
    fontSize: 18,
    fontWeight: '600',
  },
  activeBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  activeBadgeText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '600',
  },
  locationActions: {
    flexDirection: 'row',
    gap: 8,
  },
  actionButton: {
    width: 36,
    height: 36,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  locationAddress: {
    fontSize: 14,
    marginBottom: 4,
  },
  locationCoords: {
    fontSize: 12,
    fontFamily: Platform.OS === 'ios' ? 'Courier' : 'monospace',
    marginBottom: 4,
  },
  locationNotes: {
    fontSize: 14,
    fontStyle: 'italic',
    marginTop: 4,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '90%',
    paddingBottom: Platform.OS === 'ios' ? 40 : 20,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  closeButton: {
    padding: 4,
  },
  modalBody: {
    padding: 20,
  },
  inputGroup: {
    marginBottom: 16,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
  },
  coordRow: {
    flexDirection: 'row',
    gap: 12,
  },
  coordInput: {
    flex: 1,
  },
  textArea: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  useCurrentButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 12,
    borderRadius: 8,
    marginBottom: 16,
    gap: 8,
  },
  useCurrentButtonText: {
    color: '#fff',
    fontWeight: '600',
  },
  modalFooter: {
    flexDirection: 'row',
    gap: 12,
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
  },
  cancelButton: {
    flex: 1,
    padding: 14,
    borderRadius: 8,
    borderWidth: 1,
    alignItems: 'center',
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  saveButton: {
    flex: 1,
    padding: 14,
    borderRadius: 8,
    alignItems: 'center',
  },
  saveButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
});

export default ClubLocationManager;
